import { useLocation } from 'react-router-dom';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { useRudderstackReauth } from '@/_shared/hooks/useRudderstackReauth';

import type { WarehousePermission } from './accountCreatePermission';

/**
 * A fresh RudderStack sign-in that comes back to this page. With Lookout's own sign-in on, the OAuth
 * start only reaches /login, which drops a signed-in user's `return_to`, so that path signs out first.
 */
export function RudderstackSignInButton(): React.JSX.Element {
  const { pathname, search } = useLocation();
  const reauth = useRudderstackReauth({ returnTo: `${pathname}${search}` });
  return reauth.native || reauth.isLoading ? (
    <Button
      type="button"
      variant="secondary"
      loading={reauth.pending || reauth.isLoading}
      onClick={reauth.start}
      label="Sign in with RudderStack"
    />
  ) : (
    <Button variant="secondary" asChild>
      <a href={reauth.oauthUrl}>Sign in with RudderStack</a>
    </Button>
  );
}

/** The recovery control; callers decide when to show it and own its surrounding layout. */
export function PermissionAction({ permission }: { permission: WarehousePermission }): React.JSX.Element {
  return permission.reauthRequired ? (
    <RudderstackSignInButton />
  ) : (
    <Button type="button" variant="secondary" onClick={permission.retry} label="Try again" />
  );
}

/**
 * ACT2-1276: why the RudderStack permission blocks the warehouse actions, with the one thing the user
 * can do about it here: try again, or sign in with RudderStack again. Nothing while the answer loads.
 */
export function PermissionNotice({
  permission,
  className,
}: {
  permission: WarehousePermission;
  className?: string;
}): React.JSX.Element | null {
  if (permission.loading || permission.blocked === null) return null;
  return (
    <Callout variant="warning" className={className}>
      <p>{permission.blocked}</p>
      {permission.reauthRequired || permission.unknown ? (
        <div className="mt-2">
          <PermissionAction permission={permission} />
        </div>
      ) : null}
    </Callout>
  );
}
